import { FoodsIcon, FoodsSelect, FoodsTextInput, Textarea, WhiteBox } from "@foodpilot/foods";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useUpsertStrategy } from "src/api/client-api/csrStrategy/useUpsertStrategy";
import { Breadcrumbs } from "src/components/Breadcrumb/Breadcrumbs";
import { CREATE, MODIFY, STRATEGY, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useSettingsStrategy } from "src/context/Settings/CsrStrategy/useSettingsStrategy";
import { URLHandler } from "src/services/URLHandler";
import { useSnackbar } from "src/utils/useSnackbar";
import { Pillars } from "./Pillars/Pillars";
import { formToStrategyWrite, strategyToForm } from "./StrategyOverview.mappers";

export const StrategyOverview = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const { strategy } = useSettingsStrategy();
    const { campaigns, canUser, currentCampaign } = useFoodpilotContext();
    const upsertStrategy = useUpsertStrategy();
    const currentCampaignIri =
        currentCampaign?.["@id"] ?? (currentCampaign ? `/api/campaigns/${currentCampaign.id}` : null);
    const [strategyForm, setStrategyForm] = useState(() => strategyToForm(strategy, currentCampaignIri));
    const selectedEndCampaign = campaigns.find((campaign) => campaign["@id"] === strategyForm.endCampaign) ?? null;
    const canEditStrategy = strategy === undefined ? canUser(CREATE, STRATEGY) : canUser(MODIFY, STRATEGY);
    const canSave = Boolean(strategyForm.name.trim() && strategyForm.endCampaign);

    const saveStrategy = () => {
        if (!canSave || upsertStrategy.isLoading) return;

        upsertStrategy.mutate(
            { payload: formToStrategyWrite(strategyForm) },
            {
                onSuccess: (response) => {
                    const savedId = response.data?.id;
                    if (typeof savedId === "string") {
                        setStrategyForm((current) => ({ ...current, id: savedId }));
                    }
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "info",
                        message: t("settings.success"),
                    });
                },
            },
        );
    };

    return (
        <Box
            component="main"
            sx={{
                px: { xs: 2, md: 9 },
                py: { xs: 3, md: 4.5 },
                backgroundColor: "#F8F7F5",
                minHeight: "100%",
            }}
        >
            <Stack gap={theme.spacing(5)}>
                <Stack gap={theme.spacing(1.5)}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "flex-start" }}
                        justifyContent="space-between"
                        gap={theme.spacing(2)}
                    >
                        <Stack gap={theme.spacing(1.5)} sx={{ minWidth: 0 }}>
                            <Breadcrumbs
                                items={[
                                    {
                                        label: t("settings.content.title", "Content"),
                                        action: () => navigate(URLHandler.settings.content()),
                                    },
                                    { label: t("settings.csr.title", "Strategy") },
                                ]}
                            />
                            <Typography variant="body" color={theme.custom.grey[2100]}>
                                {t(
                                    "settings.csr.overview.subtitle",
                                    "Configure your CSR strategy, its strategic pillars and their levers and associated indicators.",
                                )}
                            </Typography>
                        </Stack>
                        {canEditStrategy && (
                            <Button
                                variant="primary"
                                startIcon={<FoodsIcon icon="check" size={2} />}
                                disabled={!canSave || upsertStrategy.isLoading}
                                onClick={saveStrategy}
                                sx={{ minWidth: theme.spacing(14), alignSelf: { xs: "flex-end", sm: "flex-start" } }}
                            >
                                {t("glossary.save")}
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <Stack gap={theme.spacing(2.5)}>
                    <Typography variant="h2">{t("settings.csr.info.title")}</Typography>
                    <WhiteBox
                        borderRadius={theme.spacing(2)}
                        boxShadow="none"
                        sx={{
                            border: `1px solid ${theme.custom.grey[500]}`,
                            backgroundColor: theme.palette.common.white,
                            overflow: "hidden",
                        }}
                    >
                        <Stack gap={theme.spacing(3)} padding={{ xs: 3, md: 4 }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(240px, 300px)" },
                                    gap: theme.spacing(3),
                                    alignItems: "end",
                                }}
                            >
                                <Stack gap={theme.spacing(1)}>
                                    <Typography variant="big-medium">
                                        {t("settings.csr.info.fields.name.label") + "*"}
                                    </Typography>
                                    <FoodsTextInput
                                        value={strategyForm.name}
                                        onChange={(name) => setStrategyForm((current) => ({ ...current, name }))}
                                        ExtraInputProps={{
                                            placeholder: t(
                                                "settings.csr.overview.name_placeholder",
                                                "Enter your strategy name",
                                            ),
                                        }}
                                    />
                                </Stack>
                                <Stack gap={theme.spacing(1)}>
                                    <Typography variant="big-medium">
                                        {t("settings.csr.overview.target_campaign", "Target campaign") + "*"}
                                    </Typography>
                                    <FoodsSelect
                                        selectedOption={selectedEndCampaign}
                                        options={campaigns}
                                        onChange={(selectedValue) =>
                                            setStrategyForm((current) => ({
                                                ...current,
                                                endCampaign: selectedValue?.["@id"] ?? null,
                                            }))
                                        }
                                        getId={(item) => item.id}
                                        getName={(item) => item.label}
                                        textOptions={{
                                            placeholder: t(
                                                "settings.csr.overview.campaign_placeholder",
                                                "Select a campaign",
                                            ),
                                            searchPlaceholder: "",
                                            emptyOptionsText: "",
                                            defaultValueText: undefined,
                                            noResult: "",
                                        }}
                                    />
                                </Stack>
                            </Box>
                            <Stack gap={theme.spacing(1)}>
                                <Typography variant="big-medium">
                                    {t("settings.csr.info.fields.description.label")}
                                </Typography>
                                <Textarea
                                    value={strategyForm.description}
                                    onChange={(description) =>
                                        setStrategyForm((current) => ({ ...current, description }))
                                    }
                                    placeholder={t(
                                        "settings.csr.overview.description_placeholder",
                                        "Enter a short description of your strategy",
                                    )}
                                    minRows={3}
                                />
                            </Stack>
                        </Stack>
                    </WhiteBox>
                </Stack>

                <Box borderTop={`1px solid ${theme.custom.grey[500]}`} />
                <Pillars />
            </Stack>
        </Box>
    );
};
